import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, matchPath, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import * as sessionUserSelector from 'selectors/sessionUser'
import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

import { capitalizeFirstLetter } from 'utils/strings'

import {
  RouteTicketProviders,
  RouteTicketProviderTickets,
  RouteTicketingSystems,
  RouteTicketingSystemsByPartner,
  RouteTicketingSystemsTickets,
} from 'routes'

import NavBarMenu from './NavBarMenu/Index'
import NavBarMenuItem from './NavBarMenu/NavBarMenuItem'
import MegaMenu from './MegaMenu/Index'
import MegaMenuSection from './MegaMenu/MegaMenuSection'
import MegaMenuColumn from './MegaMenu/MegaMenuColumn'
import MegaMenuRowsGroup from './MegaMenu/MegaMenuRowsGroup'
import MegaMenuSubMenu from './MegaMenu/MegaMenuSubMenu'
import MegaMenuLinkToSubMenu from './MegaMenu/MegaMenuLinkToSubMenu'
import MegaMenuLink from './MegaMenu/MegaMenuLink'
import MegaMenuNoData from './MegaMenu/MegaMenuNoData'

const NavTickets = () => {
  const [selectedPartnerId, setSelectedPartnerId] = useState(null)

  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { ticketingSystemDisplayId } = useParams()

  const isUserPrivileged = useSelector(sessionUserSelector.isUserPrivileged)
  const providersList = useSelector(sessionUserSelector.ticketProviders) || []
  const partnersList = useSelector(sessionUserSelector.partnerTicketingSystems) || []
  const partnerTicketingSystemsById = useSelector(sessionUserSelector.partnerTicketingSystemsById)
  let ticketingSystemsList = useSelector(ticketingSystemsSelector.ticketingSystems)

  const isActiveNavTab = Boolean(
    matchPath({ path: RouteTicketProviders(), end: false }, location.pathname) ||
    matchPath({ path: RouteTicketingSystems(), end: false }, location.pathname)
  )

  let partnerId = searchParams.get('partnerId') || null
  if (isUserPrivileged && isActiveNavTab && !partnerId && ticketingSystemDisplayId) {
    partnerId = partnerTicketingSystemsById[ticketingSystemDisplayId]?.partner.id
  }

  ticketingSystemsList = ticketingSystemsList.map(ts => {
    return ({
      id: ts.id,
      name: ts.name,
      to: RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ts.displayId }),
    })
  })

  useEffect(() => {
    setSelectedPartnerId(partnerId)
  }, [partnerId])

  if (!ticketingSystemsList.length && !isUserPrivileged) {
    return null
  }

  const handlePartnerClick = (partnerId) => {
    setSelectedPartnerId(partnerId)
  }

  const handleCloseMegaMenu = () => {
    setSelectedPartnerId(partnerId || null)
  }

  const providerSection = () => {
    return (
      <MegaMenuSection title={t('mainHeader.ticketsSectionProvider')}>
        <MegaMenuColumn>
          <MegaMenuRowsGroup title={t('mainHeader.ticketsSectionProviders')} to={RouteTicketProviders()}>
            {!partnersList.length &&
              <MegaMenuNoData />
            }
            {providersList.map((provider) => {
              return (
                <MegaMenuLink
                  key={provider.id}
                  name={provider.name}
                  to={RouteTicketProviderTickets({ ticketProviderId: provider.id })}
                />
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>
      </MegaMenuSection>
    )
  }

  const partnerSection = () => {
    return (
      <MegaMenuSection title={t('mainHeader.ticketsSectionPartner')}>
        <MegaMenuColumn>
          <MegaMenuRowsGroup title={t('mainHeader.partners')} isHighlighted>
            {!partnersList.length &&
              <MegaMenuNoData />
            }
            {partnersList.map((partner) => {
              return (
                <MegaMenuLinkToSubMenu
                  key={partner.id}
                  name={capitalizeFirstLetter(partner.name)}
                  itemId={partner.id}
                  onClickHandler={handlePartnerClick}
                  to={RouteTicketingSystemsByPartner({ partnerId: partner.id })}
                  active={partner.id === selectedPartnerId}
                />
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>

        <MegaMenuColumn>
          <MegaMenuRowsGroup title={t('mainHeader.configTicketingSystems')}>
            {partnersList.map((partner) => {
              return (
                <MegaMenuSubMenu
                  key={partner.id}
                  visible={partner.id === selectedPartnerId}
                >
                  {!partner.ticketingSystems.length &&
                    <MegaMenuNoData />
                  }
                  {partner.ticketingSystems.map((ts) => {
                    return (
                      <MegaMenuLink
                        key={ts.id}
                        name={ts.name}
                        to={RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ts.id })}
                      />
                    )
                  })}
                </MegaMenuSubMenu>
              )
            })}
          </MegaMenuRowsGroup>
        </MegaMenuColumn>
      </MegaMenuSection>
    )
  }

  return (
    isUserPrivileged
      ? (
        <MegaMenu
          active={isActiveNavTab}
          title={t('mainHeader.tickets')}
          handleClose={handleCloseMegaMenu}
        >
          {partnerSection()}
          {providerSection()}
        </MegaMenu>
        )
      : (
        <NavBarMenu active={isActiveNavTab} title={t('mainHeader.tickets')}>
          {ticketingSystemsList.map((page) => {
            return <NavBarMenuItem key={page.name} name={page.name} to={page.to} />
          })}
        </NavBarMenu>
        )
  )
}

export default NavTickets
